/**
 * EnrichmentProgressPanel Component
 *
 * Individual panel showing enrichment progress for a single operation.
 * Features:
 * - Real-time progress updates
 * - Phase indicators (searching, found, failed, skipped)
 * - Elapsed time tracking
 * - Cancel button
 * - Collapsible/expandable states
 *
 * Pattern follows: RouteCalculationToast.tsx
 */

import React from 'react';
import { X, ChevronDown, ChevronUp, Loader2, Check, AlertCircle, Search, XCircle } from 'lucide-react';
import type { EnrichmentOperation, EnrichmentPhase } from '../../store/enrichmentProgressStore';

// Phase icons and colors
const PHASE_CONFIG: Record<EnrichmentPhase, { icon: React.ReactNode; color: string; label: string }> = {
  idle: { icon: null, color: 'text-dark-muted', label: 'Waiting...' },
  connecting: { icon: <Loader2 className="w-3 h-3 animate-spin" />, color: 'text-brand-400', label: 'Connecting...' },
  searching: { icon: <Search className="w-3 h-3 animate-pulse" />, color: 'text-brand-400', label: 'Searching...' },
  found: { icon: <Check className="w-3 h-3" />, color: 'text-green-400', label: 'Found!' },
  skipped: { icon: <XCircle className="w-3 h-3" />, color: 'text-yellow-400', label: 'Skipped' },
  failed: { icon: <AlertCircle className="w-3 h-3" />, color: 'text-red-400', label: 'Failed' },
};

interface EnrichmentProgressPanelProps {
  operation: EnrichmentOperation;
  onToggleExpanded: () => void;
  onToggleMinimized: () => void;
  onCancel: () => void;
  onDismiss: () => void;
}

/**
 * Format elapsed time as MM:SS
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get progress percentage
 */
const getProgressPercent = (current: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((current / total) * 100);
};

export const EnrichmentProgressPanel: React.FC<EnrichmentProgressPanelProps> = ({
  operation,
  onToggleExpanded,
  onToggleMinimized,
  onCancel,
  onDismiss,
}) => {
  const { status, currentIndex, totalCount, currentEvent, enrichedCount, failedCount, elapsedSeconds, isExpanded, error } = operation;
  const progressPercent = getProgressPercent(currentIndex, totalCount);

  // Status-specific styles
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return 'border-green-500/50';
      case 'failed':
        return 'border-red-500/50';
      case 'cancelled':
        return 'border-yellow-500/50';
      default:
        return 'border-brand-500/50';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <Check className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-yellow-400" />;
      default:
        return <Loader2 className="w-5 h-5 text-brand-400 animate-spin" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'completed':
        return 'Enrichment Complete!';
      case 'failed':
        return 'Enrichment Failed';
      case 'cancelled':
        return 'Enrichment Cancelled';
      default:
        return 'Enriching Events...';
    }
  };

  const getStatusTextColor = () => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'cancelled':
        return 'text-yellow-400';
      default:
        return 'text-brand-400';
    }
  };

  return (
    <div className={`bg-dark-surface border ${getStatusStyles()} rounded-xl shadow-2xl shadow-black/20 overflow-hidden min-w-[320px] max-w-[380px]`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-dark-header/50">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-full bg-dark-surface/50">
            {getStatusIcon()}
          </div>
          <span className={`text-sm font-medium ${getStatusTextColor()}`}>
            {getStatusText()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Minimize button */}
          <button
            onClick={onToggleMinimized}
            className="p-2 rounded-full hover:bg-dark-surface/50 transition-all duration-150"
            aria-label="Minimize"
            title="Minimize to pill"
          >
            <ChevronDown className="w-4 h-4 text-dark-muted" />
          </button>
          {/* Dismiss button */}
          <button
            onClick={onDismiss}
            className="p-2 rounded-full hover:bg-dark-surface/50 transition-all duration-150"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-dark-muted" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3">
        {/* Current event info */}
        {status === 'running' && currentEvent && (
          <div className="mb-3">
            <div className="text-xs text-dark-muted mb-1">
              ({currentIndex}/{totalCount} events) Enriching...
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-dark-text font-medium truncate max-w-[250px]">
                "{currentEvent.name}"
              </span>
            </div>
            <div className={`flex items-center gap-1.5 mt-1 ${PHASE_CONFIG[currentEvent.phase].color}`}>
              {PHASE_CONFIG[currentEvent.phase].icon}
              <span className="text-xs">{PHASE_CONFIG[currentEvent.phase].label}</span>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
            {error}
          </div>
        )}

        {/* Progress bar */}
        <div className="h-2 bg-dark-surface/50 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-200 ease-out ${
              status === 'completed' ? 'bg-green-500' :
              status === 'failed' ? 'bg-red-500' :
              status === 'cancelled' ? 'bg-yellow-500' :
              'bg-brand-500'
            }`}
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>

        {/* Progress details */}
        <div className="flex justify-between items-center mt-2">
          <span className={`text-xs font-medium ${getStatusTextColor()}`}>
            {progressPercent}%
          </span>
          <span className="text-xs text-dark-muted">
            {formatTime(elapsedSeconds)}
          </span>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-400" />
            <span className="text-green-400">{enrichedCount} enriched</span>
          </div>
          {failedCount > 0 && (
            <div className="flex items-center gap-1">
              <XCircle className="w-3 h-3 text-red-400" />
              <span className="text-red-400">{failedCount} failed</span>
            </div>
          )}
        </div>

        {/* Cancel button (only when running) */}
        {status === 'running' && (
          <button
            onClick={onCancel}
            className="mt-3 w-full px-3 py-2 text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/30 rounded hover:bg-red-500/20 transition-all duration-150"
          >
            Cancel Enrichment
          </button>
        )}

        {/* Navigate hint (when running) */}
        {status === 'running' && (
          <p className="text-[11px] text-dark-muted mt-2 italic text-center">
            Navigate freely - progress will persist
          </p>
        )}
      </div>
    </div>
  );
};

export default EnrichmentProgressPanel;
